import {
  FUND_BASKET_STRATEGY_OPTIONS,
  FUND_BASKET_TENURE_OPTIONS,
} from "creso-core/constants";
import { useFundBasket } from "creso-core/hooks";
import {
  FundBasket,
  FundBasketFundInput,
  FundBasketStrategy,
  FundBasketTenure,
} from "creso-core/types";
import { useForm } from "react-hook-form";
import {
  buttonVariants,
  DialogContent,
  DialogTrigger,
  MultiStepFormV2,
  V2Dialog,
  V2Loader,
} from "web-design-system";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { useMemo, useState } from "react";
import { BasketSetup } from "./basket-setup";
import { BasketFunds } from "./basket-funds";
import { BasketRebalanceReview } from "./basket-rebalance-review";

export type FundBasketFormValues = {
  id?: number;
  name: string;
  tenure: FundBasketTenure | null;
  strategy: FundBasketStrategy | null;
  funds: (FundBasketFundInput & {
    name: string;
    provider_company_id?: string;
  })[];
};

export function FundBasketDialog(props: {
  basketId?: number;
  variant?: "default" | "outline";
  trigger?: (props: { onClick: () => void }) => React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultStep?: number;
}) {
  const { trigger: Trigger } = props;
  const { data: basket, isLoading } = useFundBasket(props.basketId as number, {
    enabled: !!props.basketId,
  });

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = props.open !== undefined;
  const open = isControlled ? !!props.open : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    props.onOpenChange?.(next);
  };

  return (
    <V2Dialog open={open} onOpenChange={setOpen}>
      {isControlled ? null : Trigger ? (
        <Trigger onClick={() => setOpen(true)} />
      ) : (
        <DialogTrigger
          onClick={() => setOpen(true)}
          className={buttonVariants({ variant: props.variant })}
        >
          Create Basket
        </DialogTrigger>
      )}
      <DialogContent className="h-170 p-0 max-w-250 w-[75vw] outline-0 flex flex-col overflow-hidden">
        <V2Loader className="min-h-0" isLoading={isLoading}>
          <FundBasketForm
            basket={basket}
            defaultStep={props.defaultStep}
            onComplete={() => setOpen(false)}
          />
        </V2Loader>
      </DialogContent>
    </V2Dialog>
  );
}

export function FundBasketForm(props: {
  basket?: FundBasket;
  onComplete: () => void;
  defaultStep?: number;
}) {
  const config = useMemo(
    () => [
      {
        id: "name",
        label: (values: FundBasketFormValues) => `${values.name}`,
        description: (values: FundBasketFormValues) =>
          values.tenure && values.strategy ? (
            <>
              {
                FUND_BASKET_TENURE_OPTIONS.find(
                  (option) => option.value === values.tenure
                )?.label
              }
              &nbsp;&bull;&nbsp;
              {
                FUND_BASKET_STRATEGY_OPTIONS.find(
                  (option) => option.value === values.strategy
                )?.label
              }
            </>
          ) : (
            <>Name your basket and set its attributes.</>
          ),
        component: BasketSetup,
        isComplete: () => true,
        isNavigable: () => true,
      },
      {
        id: "configuration",
        label: () => "Configuration",
        component: BasketFunds,
        additionalInfo: (basket: FundBasketFormValues) => {
          const funds = basket?.funds.filter((f) => !!f.provider_company_id);
          if (funds.length) {
            const logo = Array.from(
              new Set(funds.map((f) => f.provider_company_id).filter(Boolean))
            ).slice(0, 3) as string[];
            return (
              <div className="flex pl-6.5 flex-col gap-2 pt-3 bg-transparent rounded">
                <div className="flex gap-2 items-center">
                  <div className="flex -space-x-1.5">
                    {logo.map((l) => (
                      <AmcLogo
                        key={l}
                        className="border overflow-hidden z-1 flex items-center justify-center border-border rounded-md"
                        amc_code={l}
                      />
                    ))}
                  </div>
                  <span className="text-sm">{funds.length} fund(s)</span>
                </div>
              </div>
            );
          }
          return (
            <div className="flex p-6 mt-3 text-xs bg-card items-center text-muted-foreground justify-center">
              No schemes added
            </div>
          );
        },
        isNavigable: (form: FundBasketFormValues) =>
          !!form.tenure && !!form.strategy && !!form.name,
        isComplete: (form: FundBasketFormValues) =>
          form.funds.filter((f) => f.fund_scheme !== -1).length > 0,
      },

      {
        id: "review-sips",
        label: () => "Review matching SIPs",
        component: BasketRebalanceReview,
        show: (form: FundBasketFormValues) => !!form.id,
        isNavigable: () => false,
        isComplete: () => false,
      },
    ],
    []
  );

  const form = useForm<FundBasketFormValues>({
    defaultValues: {
      id: props.basket?.id,
      name: props.basket?.name ?? "Mutual Fund Basket",
      tenure: props.basket?.tenure ?? null,
      strategy: props.basket?.strategy ?? null,
      funds: props.basket?.funds.map((fund) => ({
        fund_scheme: fund.fund_scheme_id,
        allocation_percentage: parseFloat(fund.allocation_percentage),
        name: fund.fund_scheme_name,
        provider_company_id: fund.provider_company_id,
      })) ?? [
        {
          fund_scheme: -1,
          allocation_percentage: 0,
          name: "",
          provider_company_id: "",
        },
      ],
    },
  });

  return (
    <MultiStepFormV2
      title={props.basket ? "Edit Fund Basket" : "Create Fund Basket"}
      config={config}
      onComplete={props.onComplete}
      defaultStep={props.defaultStep ?? 0}
      form={form}
    />
  );
}
